/** UPLOAD BUTTON
 * + Dropzone
 */

import { useCallback, useMemo, useState } from "react"
import Dropzone from "react-dropzone"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCloudUploadAlt } from "@fortawesome/free-solid-svg-icons"
import Button from "@/components/button"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { withTranslation, WithTranslation } from "react-i18next"
import Space from "@/components/space"
import { toast } from "react-toastify"

interface OwnProps {
  onUpload: (files: File[]) => Promise<any> | any
  acceptedFiles?: string
  maxSize?: number
  title?: string
  showFrameStyle?: boolean
}

type Props = OwnProps & WithTranslation

function UploadButtonWidget(props: Props) {
  const { t, title, showFrameStyle = false } = props
  const [status, setStatus] = useState<string>(STATUS_LOADED)

  // Si tu veux que acceptedFiles/maxSize se mettent à jour si les props changent,
  // évite useState et utilise useMemo.
  const acceptedFilesExt = useMemo(
    () => (props.acceptedFiles ? props.acceptedFiles : ".csv,.xls,.xlsx"),
    [props.acceptedFiles]
  )
  const maxSize = useMemo(() => (props.maxSize ? props.maxSize : 2000000), [props.maxSize])

  // Normalise les entrées: Dropzone envoie File[], input file envoie un event
  const normalizeToFiles = (filesOrEvent: File[] | any): File[] => {
    if (Array.isArray(filesOrEvent)) return filesOrEvent
    return Array.from(filesOrEvent?.target?.files || [])
  }

  const uploadFile = useCallback(
    async (filesOrEvent: File[] | any) => {
      const files = normalizeToFiles(filesOrEvent)
      if (!files.length) return

      setStatus(STATUS_LOADING)
      try {
        await props.onUpload(files)
      } finally {
        setStatus(STATUS_LOADED)
      }
    },
    [props.onUpload]
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      uploadFile(acceptedFiles)
    },
    [uploadFile]
  )

  const onDropRejected = useCallback(
    (rejectedFiles: any) => {
      toast(t(rejectedFiles[0].errors[0].message), { type: "error" })
    },
    [t]
  )

  return (
    <>
      {title && (
        <div className="text-input-title flex">
          <span className="grey-t">{title}</span>
        </div>
      )}

      <Dropzone onDrop={onDrop} onDropRejected={onDropRejected} accept={acceptedFilesExt} maxSize={maxSize}>
        {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
          <div
            className={
              showFrameStyle
                ? `flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 transition ${
                    isDragActive ? "border-cyan-500 bg-cyan-50" : "border-gray-300"
                  }`
                : "flex1 flex grey-b upload-button" + (isDragActive ? " upload-button-drop" : "")
            }
            {...getRootProps()}>
            <input {...getInputProps()} />

            {!showFrameStyle && <div className="flex1"></div>}

            <div className="flex1">
              <div className={`${showFrameStyle ? "text-3xl text-cyan-500 mb-2 text-center" : "adm-upload-icon flex"}`}>
                <FontAwesomeIcon icon={faCloudUploadAlt} />
              </div>

              {isDragActive ? (
                <div style={{ textAlign: "center" }}>
                  <p>{t("file_drop_zone") + ""}</p>
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <p className="grey-t">{t("click_or_drop")}</p>
                  {!showFrameStyle && <div className="height-20" />}
                  <div className="flex">
                    <Space />
                    <Button className="primary" isLarge={!showFrameStyle} isLoading={status === STATUS_LOADING}>
                      <input
                        type="file"
                        id="myFile"
                        name="file"
                        multiple={false}
                        onChange={isDragReject ? () => {} : uploadFile}
                        accept={acceptedFilesExt}
                      />
                      {t("adm_upload_btn_action")}
                    </Button>
                    <Space />
                  </div>
                </div>
              )}
            </div>

            {!showFrameStyle && <div className="flex1"></div>}
          </div>
        )}
      </Dropzone>
    </>
  )
}

export default withTranslation()(UploadButtonWidget)
